import React from "react";
import { Button, Row, Col, Dropdown } from "react-bootstrap";
import { AdminUser, DeviceBase } from "@preveil-api";
import {
  Account, Device, DevicePlatform, DeviceStatus, Message, MessageHandlerDisplayType, MessageToastTypes, useAppDispatch, useLockDeviceMutation, useUnlockDeviceMutation,
  useDeleteDeviceMutation, AdminMessages, AdminErrorMessages, AdminSuccessMessages, DeviceAction, MessageAnchors, getDeviceStatus, DeviceMessageTooltip
} from "src/common";
import { Icon } from "src/components";
import { uiActions } from "src/store";

type AllProps = {
  device: DeviceBase;
  current_account: Account;
  org_user: AdminUser;
  refresh: Function;
};

function DeviceRowComponent(props: AllProps) {
  const { device, org_user, current_account, refresh } = props;
  const dispatch = useAppDispatch();
  const device_status_label = device.is_active ? "Lock" : "Unlock";
  const device_status_action = device.is_active ? "lock" : "unlock";
  const lock_unlock_tooltip = DeviceMessageTooltip[device_status_action];
  const delete_device_tooltip = DeviceMessageTooltip.delete;
  const device_status = getDeviceStatus(device);
  const disabled = device_status === DeviceStatus.pending_delete || device_status === DeviceStatus.deleted;

  const [lockDevice] = useLockDeviceMutation();
  const [unlockDevice] = useUnlockDeviceMutation();
  const [deleteDevice] = useDeleteDeviceMutation();

  // Description: Handles Lock or Unlock Device Request.
  async function lockOrUnlockDevice(device_action: string, device: DeviceBase, user_email: string) {
    let query;
    if (device_action === DeviceAction.lock) {
      query = lockDevice({ userId: current_account.user_id, deviceId: device.device_id, forUser: user_email });
    } else if (device_action === DeviceAction.unlock) { 
      query = unlockDevice({ userId: current_account.user_id, deviceId: device.device_id, forUser: user_email });
    }

    if (!query) {
      // Note: Safeguarding in the unlikely case that the device_action is not lock or unlock.
      const message = AdminErrorMessages.default;
      dispatch(uiActions.handleRequestErrors(new Message(message)));
    } else {
      const message = device_action === DeviceAction.lock ? AdminSuccessMessages.success_locked_device.replace(MessageAnchors.user_id, device.device_name) : AdminSuccessMessages.success_unlocked_device.replace(MessageAnchors.user_id, device.device_name);
      query
      .unwrap()
      .then(() => {
        refresh(); // we need to refresh the device list
        dispatch(uiActions.handleSetMessage(new Message(message)));
      })
      .catch((stack) => {
        const error_message = device_action === DeviceAction.lock ? AdminErrorMessages.error_locking_device.replace(MessageAnchors.user_id, device.device_name) : AdminErrorMessages.error_unlocking_device.replace(MessageAnchors.user_id, device.device_name);
        dispatch(uiActions.handleRequestErrors(new Message(error_message), stack));
      });
    }
  }

  // Description: Handles Delete Device Request
  async function deleteDeviceRequest(device: DeviceBase, user_email: string) {
    const { device_id, device_name } = device;
    const query = deleteDevice({ user_id: current_account.user_id, device_id, for_user: user_email });
    query
      .unwrap()
      .then(() => {
        refresh();
        const message = AdminSuccessMessages.success_deleted_device.replace(MessageAnchors.user_id, device_name);
        dispatch(uiActions.handleSetMessage(new Message(message)));
      })
      .catch((stack) => {
        const message = AdminErrorMessages.error_deleting_device.replace(MessageAnchors.user_id, device_name);
        dispatch(uiActions.handleRequestErrors(new Message(message), stack));
      });
  }

  /* Description: handles the following actions: Lock, Unlock, Delete */
  function handleDeviceAction(device_action: string) {
    let message = "";
    let title = "";
    let button_label = "";
    switch(device_action) { 
      case DeviceAction.lock: { 
        message = AdminMessages.confirm_device_lock.body.replace(MessageAnchors.message_content, device.device_name);
        title = AdminMessages.confirm_device_lock.title;
        button_label = "Lock";
        break; 
      } 
      case DeviceAction.unlock: { 
        message = AdminMessages.confirm_device_unlock.body.replace(MessageAnchors.message_content, device.device_name);
        title = AdminMessages.confirm_device_unlock.title;
        button_label = "Unlock";
        break; 
      }
      case DeviceAction.delete: {
        message = AdminMessages.confirm_device_delete.body.replace(MessageAnchors.message_content, device.device_name);
        title = AdminMessages.confirm_device_delete.title;
        button_label = "Delete";
        break;
      }
    }
    const confirmation_dialog = new Message(
      message,
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      title,
      {
        label: button_label,
        data: true,
        action: () => {
          return device_action === DeviceAction.delete ? deleteDeviceRequest(device, org_user.user_id) : lockOrUnlockDevice(device_status_action, device, org_user.user_id);
        }
      },
      { label: "Cancel" },
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  return <Row>
    <Col sm={4} md={6} className="name-with-icon">
      <Icon
        className={`pv-icon-${DevicePlatform[device.platform].class || DevicePlatform.default.class
          } device-icon`}
      ></Icon>
      <div>{device.device_name}</div>
    </Col>
    <Col sm={3} md={4}>
      <Button
        variant="transparent"
        className={`notification-count ${Device.deviceStatusColor(device_status.class)}`}
        aria-label="Close"
      >
        {device_status.text}
      </Button>
    </Col>
    <Col sm="auto" className="ms-auto">
      <Dropdown as="button" className="btn btn-icon btn-sm pv-dropdown" disabled={disabled}>
        <Dropdown.Toggle as="span">
          <Icon className="pv-icon-more"></Icon>
        </Dropdown.Toggle>
        <Dropdown.Menu align="end">
          <Dropdown.Item 
            data-tooltip-id="pv-tooltip"
            data-tooltip-content={lock_unlock_tooltip}
            data-tooltip-place="left"
            onClick={() => handleDeviceAction(device_status_action)}>{device_status_label}</Dropdown.Item>
          <Dropdown.Item
            data-tooltip-id="pv-tooltip"
            data-tooltip-content={delete_device_tooltip}
            data-tooltip-place="left"
            onClick={() => handleDeviceAction(DeviceAction.delete)}>Delete</Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </Col>
  </Row>;
}

export default React.memo(DeviceRowComponent);
